/**
 * F606ProductsAdmin — inline price/stock editing + hide/unhide.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Loader2, EyeOff, Eye, Save, X } from 'lucide-react';
import { bridge, describeActions } from './bridgeClient';

export default function F606ProductsAdmin({ language }) {
  const isAr = language === 'ar';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [editing, setEditing] = useState({});   // { id: {price, stock} }

  const load = useCallback(async () => {
    setLoading(true);
    const res = await bridge.list('products', { limit: 100 });
    if (res.success !== false) setRows(res.data || []);
    else toast.error(res.error?.message || 'Failed to load products');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (p, fn, label, body = {}) => {
    setBusy((s) => ({ ...s, [p.id]: label }));
    const res = await fn(p.id, body);
    setBusy((s) => ({ ...s, [p.id]: null }));
    if (res.success) {
      toast.success(`✅ ${label} · ${describeActions(res.actions_fired, language)}`, { duration: 5000 });
      setEditing((s) => ({ ...s, [p.id]: undefined }));
      load();
    } else {
      toast.error(`⚠️ ${res.error?.message || 'Failed'}`);
    }
  };

  return (
    <div className="space-y-3" data-testid="f606-products-admin">
      <div className="flex justify-between items-center">
        <div className="text-white/60 text-xs">{rows.length} {isAr ? 'منتج' : 'products'}</div>
        <button onClick={load} className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/5 text-white/50 text-[10px] uppercase tracking-wider">
              <th className="px-3 py-2 text-start">{isAr ? 'المنتج' : 'Product'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'السعر' : 'Price'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'المخزون' : 'Stock'}</th>
              <th className="px-3 py-2 text-center">{isAr ? 'الحالة' : 'Status'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'الإجراءات' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && rows.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-400/50" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-white/40">{isAr ? 'لا توجد منتجات' : 'No products'}</td></tr>
            )}
            {rows.map((p) => {
              const bId = busy[p.id];
              const isEditing = editing[p.id];
              const active = p.is_active !== false;
              return (
                <tr key={p.id} className="hover:bg-white/[0.02]" data-testid={`f606-product-row-${p.id}`}>
                  <td className="px-3 py-2 text-white">{p.name || (isAr ? p.name_ar : p.name_en) || p.name_en || '—'}</td>
                  <td className="px-3 py-2 text-end font-mono" dir="ltr">
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={isEditing.price}
                        onChange={(e) => setEditing((s) => ({ ...s, [p.id]: { ...isEditing, price: e.target.value } }))}
                        className="w-20 px-2 py-1 bg-black/40 border border-white/10 rounded text-emerald-300 text-end"
                      />
                    ) : (
                      <span className="text-emerald-300">{(p.price ?? p.price_aed) ?? '—'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end font-mono" dir="ltr">
                    {isEditing ? (
                      <input
                        type="number"
                        value={isEditing.stock}
                        onChange={(e) => setEditing((s) => ({ ...s, [p.id]: { ...isEditing, stock: e.target.value } }))}
                        className="w-16 px-2 py-1 bg-black/40 border border-white/10 rounded text-white text-end"
                      />
                    ) : (
                      <span className={Number(p.stock ?? 0) <= 3 ? 'text-amber-300' : 'text-white'}>{p.stock ?? '—'}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                      active ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-neutral-500/15 border-neutral-500/40 text-neutral-300'
                    }`}>
                      {active ? (isAr ? 'ظاهر' : 'Visible') : (isAr ? 'مخفي' : 'Hidden')}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {isEditing ? (
                        <>
                          <button
                            disabled={bId}
                            onClick={() => {
                              const body = {};
                              if (isEditing.price !== '') body.price_aed = Number(isEditing.price);
                              if (isEditing.stock !== '') body.stock = Number(isEditing.stock);
                              act(p, bridge.product.update, isAr ? 'حفظ' : 'Save', body);
                            }}
                            className="px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-[10px] font-bold disabled:opacity-40"
                            data-testid={`f606-product-save-${p.id}`}
                          >
                            <Save className="h-3 w-3 inline" /> {isAr ? 'حفظ' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditing((s) => ({ ...s, [p.id]: undefined }))}
                            className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 text-[10px]"
                          >
                            <X className="h-3 w-3 inline" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditing((s) => ({ ...s, [p.id]: { price: String((p.price ?? p.price_aed) ?? ''), stock: String(p.stock ?? '') } }))}
                            className="px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 text-[10px] font-bold disabled:opacity-40"
                            data-testid={`f606-product-edit-${p.id}`}
                          >
                            {isAr ? 'تعديل' : 'Edit'}
                          </button>
                          {active ? (
                            <button
                              disabled={bId}
                              onClick={() => act(p, bridge.product.hide, isAr ? 'إخفاء' : 'Hide')}
                              className="px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 text-[10px] font-bold disabled:opacity-40"
                              data-testid={`f606-product-hide-${p.id}`}
                            >
                              <EyeOff className="h-3 w-3 inline" /> {isAr ? 'إخفاء' : 'Hide'}
                            </button>
                          ) : (
                            <button
                              disabled={bId}
                              onClick={() => act(p, bridge.product.unhide, isAr ? 'إظهار' : 'Unhide')}
                              className="px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-[10px] font-bold disabled:opacity-40"
                              data-testid={`f606-product-unhide-${p.id}`}
                            >
                              <Eye className="h-3 w-3 inline" /> {isAr ? 'إظهار' : 'Unhide'}
                            </button>
                          )}
                        </>
                      )}
                      {bId && <Loader2 className="h-3 w-3 animate-spin text-emerald-400/70" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
